import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useTranslation } from 'react-i18next';
import { parseSmartMonthYearInput } from '../../../../Utils/parseSmartDateInput';
import styles from './CustomMonthDatePicker.module.scss';

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;

const toUtcMonthStart = (year, monthIndex) =>
    new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0, 0));

const parseDate = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMonthYear = (value) => {
    const date = parseDate(value);
    if (!date) return '';
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = String(date.getUTCFullYear());
    return `${month}/${year}`;
};

const toUtcFromSmartMonth = (localMonthStart) => {
    if (!localMonthStart) return null;
    return toUtcMonthStart(localMonthStart.getFullYear(), localMonthStart.getMonth());
};

/**
 * Single MM/YYYY month picker (image style): label, light gray rounded field, calendar icon.
 * API: date + onDateChange (UTC start of selected month).
 * Calendar flow: pick month first (commits immediately with the current year), then year.
 * Smart Enter/blur: "6"→06/currentYear, "0926"→09/2026, "092026"→09/2026.
 */
function CustomMonthDatePicker({
    date,
    onDateChange,
    label,
    direction,
    height = '48px',
    width = '100%',
    borderRadius,
    disabled = false,
    smartParseOnEnter = true,
}) {
    const { i18n } = useTranslation();
    const dir = direction || i18n.dir();
    const current = parseDate(date);
    const selectedYear = current ? current.getUTCFullYear() : new Date().getUTCFullYear();
    const selectedMonth = current ? current.getUTCMonth() + 1 : new Date().getUTCMonth() + 1;

    const rootRef = useRef(null);
    const listRef = useRef(null);
    const scrolledToSelectedRef = useRef(false);
    const justFocusedRef = useRef(false);

    const [inputValue, setInputValue] = useState(formatMonthYear(date));
    const [focused, setFocused] = useState(false);
    const [openView, setOpenView] = useState(null); // 'month' | 'year' | null
    const [menuStyle, setMenuStyle] = useState(null);
    const [draftYear, setDraftYear] = useState(selectedYear);
    const [draftMonth, setDraftMonth] = useState(selectedMonth);

    useEffect(() => {
        if (!focused) setInputValue(formatMonthYear(date));
    }, [date, focused]);

    useEffect(() => {
        setDraftYear(selectedYear);
        setDraftMonth(selectedMonth);
    }, [selectedYear, selectedMonth]);

    const yearOptions = useMemo(
        () =>
            Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, index) => {
                const value = YEAR_MIN + index;
                return { value, label: String(value) };
            }).reverse(),
        [],
    );

    const monthOptions = useMemo(
        () =>
            Array.from({ length: 12 }, (_, index) => ({
                value: index + 1,
                label: String(index + 1).padStart(2, '0'),
            })),
        [],
    );

    const updateMenuPosition = () => {
        const el = rootRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportPad = 8;
        const maxHeight = 220;
        const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
        const spaceAbove = rect.top - viewportPad;
        const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
        const heightPx = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow);

        setMenuStyle({
            position: 'fixed',
            top: openUp ? undefined : rect.bottom + 6,
            bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(heightPx, 96),
            zIndex: 1400,
        });
    };

    useLayoutEffect(() => {
        scrolledToSelectedRef.current = false;
        if (!openView) {
            setMenuStyle(null);
            return undefined;
        }
        updateMenuPosition();
        const onReposition = (event) => {
            if (event?.target && listRef.current?.contains(event.target)) return;
            updateMenuPosition();
        };
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [openView]);

    useEffect(() => {
        if (!openView) return undefined;
        const handlePointerDown = (event) => {
            const inRoot = rootRef.current?.contains(event.target);
            const inList = listRef.current?.contains(event.target);
            if (!inRoot && !inList) setOpenView(null);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpenView(null);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [openView]);

    const commitInput = (raw, { revertIfInvalid = false } = {}) => {
        const parsedLocal = smartParseOnEnter ? parseSmartMonthYearInput(raw) : null;
        const parsed = parsedLocal ? toUtcFromSmartMonth(parsedLocal) : null;
        if (!parsed) {
            if (revertIfInvalid) setInputValue(formatMonthYear(date));
            return;
        }
        setInputValue(formatMonthYear(parsed));
        onDateChange?.(parsed);
    };

    const openPicker = () => {
        if (disabled) return;
        setDraftYear(selectedYear);
        setDraftMonth(selectedMonth);
        setOpenView((currentView) => (currentView ? null : 'month'));
    };

    const handleMonthSelect = (month) => {
        const next = toUtcMonthStart(draftYear, month - 1);
        setDraftMonth(month);
        setInputValue(formatMonthYear(next));
        onDateChange?.(next);
        setOpenView('year');
        scrolledToSelectedRef.current = false;
    };

    const handleYearSelect = (year) => {
        const next = toUtcMonthStart(year, draftMonth - 1);
        setDraftYear(year);
        setInputValue(formatMonthYear(next));
        onDateChange?.(next);
        setOpenView(null);
    };

    const openOptions = openView === 'month' ? monthOptions : openView === 'year' ? yearOptions : null;
    const openSelectedValue = openView === 'month' ? draftMonth : draftYear;
    const openSelectHandler = openView === 'month' ? handleMonthSelect : handleYearSelect;

    const list =
        openView && menuStyle && openOptions
            ? createPortal(
                <div
                    ref={listRef}
                    className={styles.listPortal}
                    role="listbox"
                    style={menuStyle}
                >
                    {openOptions.map((option) => {
                        const selected = option.value === openSelectedValue;
                        return (
                            <button
                                type="button"
                                key={String(option.value)}
                                role="option"
                                aria-selected={selected}
                                className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => openSelectHandler(option.value)}
                                ref={(node) => {
                                    if (selected && node && !scrolledToSelectedRef.current) {
                                        scrolledToSelectedRef.current = true;
                                        node.scrollIntoView({ block: 'center', inline: 'nearest' });
                                    }
                                }}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>,
                document.body,
            )
            : null;

    return (
        <div
            className={`${styles.wrap} ${label ? styles.wrapWithLabel : ''} ${dir === 'rtl' ? styles.rtl : styles.ltr} ${disabled ? styles.disabled : ''}`}
            style={{ width }}
        >
            <div
                ref={rootRef}
                className={`${styles.field} ${openView ? styles.fieldOpen : ''} ${focused ? styles.fieldFocused : ''}`}
            >
                {label ? <span className={styles.notchLabel}>{label}</span> : null}
                <div className={styles.trigger} style={{ height, minHeight: height, borderRadius }}>
                    <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={label || 'Select month'}
                        aria-expanded={!!openView}
                        aria-haspopup="listbox"
                        disabled={disabled}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={openPicker}
                    >
                        <CalendarTodayOutlinedIcon className={styles.calendarIcon} />
                    </button>
                    <input
                        className={styles.valueInput}
                        value={inputValue}
                        placeholder="MM/YYYY"
                        inputMode="numeric"
                        autoComplete="off"
                        disabled={disabled}
                        aria-label={label || 'Month'}
                        onFocus={(event) => {
                            setFocused(true);
                            justFocusedRef.current = true;
                            event.target.select();
                            if (openView) setOpenView(null);
                        }}
                        onMouseUp={(event) => {
                            if (justFocusedRef.current) {
                                event.preventDefault();
                                justFocusedRef.current = false;
                            }
                        }}
                        onChange={(event) => {
                            const next = event.target.value.replace(/[^\d/]/g, '').slice(0, 7);
                            setInputValue(next);
                            const digits = next.replace(/\D/g, '');
                            if (digits.length === 6 || /^\d{1,2}\/\d{4}$/.test(next)) {
                                const parsedLocal = parseSmartMonthYearInput(next);
                                const parsed = parsedLocal ? toUtcFromSmartMonth(parsedLocal) : null;
                                if (parsed) onDateChange?.(parsed);
                            }
                        }}
                        onBlur={(event) => {
                            setFocused(false);
                            justFocusedRef.current = false;
                            commitInput(event.target.value, { revertIfInvalid: true });
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                commitInput(event.currentTarget.value, { revertIfInvalid: true });
                                event.currentTarget.blur();
                            }
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                setInputValue(formatMonthYear(date));
                                setOpenView(null);
                                event.currentTarget.blur();
                            }
                        }}
                    />
                </div>
            </div>
            {list}
        </div>
    );
}

export default CustomMonthDatePicker;
